import type { IAPIException } from "@/interfaces/api/exception/api-exception.interface.ts"
import type { BaseException } from "@/exceptions/base-exception.exception.ts"

import { OverlappingWorkPeriodException } from "@/exceptions/validation/work-period/overlapping-work-period.exception.ts"
import { GeneralValidationException } from "@/exceptions/validation/general-validation-exception.ts"
import { WorkPeriodExceptionIds } from "@/enums/exceptions/work-period-exception-ids.ts"
import { MultipleOngoingWorkPeriodsException } from "@/exceptions/validation/work-period/multiple-ongoing-work-periods.exception.ts"
import { NonChonologicalTimesException } from "@/exceptions/validation/work-period/non-chonological-times.exception.ts"
import { ActivityOutOfWorkPeriodException } from "@/exceptions/validation/work-period/activity-out-of-work-period.exception.ts"
import { FeedbackActivityOutOfWorkPeriodException } from "@/exceptions/validation/work-period/feedback-activity-out-of-work-period.exception"
import { CannotDeleteWorkPeriodWithLinkedActivitiesException } from "@/exceptions/validation/work-period/cannot-delete-work-period-with-linked-activities.exception"
import { CannotDeleteWorkPeriodWithLinkedFeedbackActivitiesException } from "@/exceptions/validation/work-period/cannot-delete-work-period-with-linked-feedback-activities.exception"
import { WorkPeriodExceedsMaxDurationException } from "@/exceptions/validation/work-period/work-period-exceeds-max-duration.exception.ts"
import { WorkPeriodDoesNotAcceptChangesException } from "@/exceptions/validation/work-period/work-period-does-not-accept-changes.exception.ts"

export function generateWorkPeriodValidationError(data: IAPIException): BaseException {
  switch (data.exceptionId) {
    case WorkPeriodExceptionIds.OVERLAPPING_WORK_PERIODS:
      return new OverlappingWorkPeriodException(data.conflictingWorkPeriods)
    case WorkPeriodExceptionIds.MULTIPLE_ONGOING_WORK_PERIODS:
      return new MultipleOngoingWorkPeriodsException(data.conflictingWorkPeriods)
    case WorkPeriodExceptionIds.NON_CHRONOLOGICAL_TIMES:
      return new NonChonologicalTimesException()
    case WorkPeriodExceptionIds.ACTIVITY_OUT_OF_WORK_PERIOD:
      return new ActivityOutOfWorkPeriodException(
        data.violatingActivities,
        data.workPeriodStart,
        data.workPeriodEnd
      )
    case WorkPeriodExceptionIds.INSTRUCTION_FEEDBACK_ACTIVITY_OUT_OF_WORK_PERIOD:
      return new FeedbackActivityOutOfWorkPeriodException(data.violatingFeedbackActivities)
    case WorkPeriodExceptionIds.CANNOT_DELETE_WORK_PERIOD_WITH_LINKED_ACTIVITIES:
      return new CannotDeleteWorkPeriodWithLinkedActivitiesException(data.linkedActivities)
    case WorkPeriodExceptionIds.CANNOT_DELETE_WORK_PERIOD_WITH_LINKED_FEEDBACK_ACTIVITIES:
      return new CannotDeleteWorkPeriodWithLinkedFeedbackActivitiesException(
        data.linkedFeedbackActivities
      )
    case WorkPeriodExceptionIds.WORK_PERIOD_EXCEEDS_MAX_DURATION:
      return new WorkPeriodExceedsMaxDurationException(
        data.maxDurationMinutes,
        data.actualDurationMinutes
      )
    case WorkPeriodExceptionIds.WORK_PERIOD_DOES_NOT_ACCEPT_CHANGES:
      return new WorkPeriodDoesNotAcceptChangesException()
    default:
      return new GeneralValidationException()
  }
}
